import {
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { GrantVestmentLikesList } from "components/list/grantvestment-likes-list";
import { ReactComponent as EyeIcon } from "./../../assets/icons/eye.svg";
import { ReactComponent as EyeClosedIcon } from "./../../assets/icons/eye-closed.svg";
import { useState, useCallback } from "react";
import * as XLSX from "xlsx";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { accent } from "style/color";

export const GrantVestmentInteractions: React.FC = () => {
  const [types, setTypes] = useState<string[]>(["Liked", "Disliked"]);
  const [showUserInput, setShowUserInput] = useState<boolean>(false);
  const [showGrantVestmentInput, setShowGrantVestmentInput] =
    useState<boolean>(false);
  const [userInput, setUserInput] = useState<string>("");
  const [grantVestmentInput, setGrantVestmentInput] = useState<string>("");
  const [tableData, setTableData] = useState<any[]>([]); // State to store table data
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [startDate, setStartDate] = useState<Date | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [formattedStartDate, setFormattedStartDate] = useState<string | null>(
    null
  );
  const [formattedEndDate, setFormattedEndDate] = useState<string | null>(null);
  const [showDateRangeInput, setShowDateRangeInput] = useState<boolean>(false);

  const formatDateToCustomFormat = (date: Date | null): string | null => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const typesFilter = [
    "Liked",
    "Disliked",
    "By User",
    "By GrantVestment",
    "By Date Range",
  ];

  const handleTypeChange = (type: string) => {
    if (type === "By User") {
      setShowUserInput((prev) => {
        if (prev) setUserInput(""); // Reset user input when hiding
        return !prev;
      });
    } else if (type === "By GrantVestment") {
      setShowGrantVestmentInput((prev) => {
        if (prev) setGrantVestmentInput(""); // Reset grant vestment input when hiding
        return !prev;
      });
    } else if (type === "By Date Range") {
      setShowDateRangeInput((prev) => {
        if (prev) {
          setStartDate(null); // Reset start date when hiding
          setEndDate(null); // Reset end date when hiding
          setFormattedStartDate(null); // Reset formatted start date
          setFormattedEndDate(null); // Reset formatted end date
        }
        return !prev;
      });
    } else {
      setTypes((prev) => {
        const index = prev.findIndex((item) => item === type);
        let filterTypes = [];

        if (index !== -1) {
          filterTypes = [...prev];
          filterTypes.splice(index, 1);
        } else {
          filterTypes = [...prev, type];
        }
        return filterTypes;
      });
    }
  };

  // Function to handle data export
  const exportToExcel = () => {
    if (tableData.length === 0) {
      alert("No data available to export.");
      return;
    }

    // Create a worksheet from the table data
    const worksheet = XLSX.utils.json_to_sheet(tableData);

    // Create a workbook and append the worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "GrantVestment Data");

    // Export the workbook as an Excel file
    XLSX.writeFile(workbook, "GrantVestmentData.xlsx");
  };

  // Callback to update table data from GrantVestmentLikesList
  // Memoize the callback to prevent it from changing on every render
  const handleTableDataUpdate = useCallback((data: any[]) => {
    setTableData(data);
  }, []);

  // Create a unique key for the GrantVestmentLikesList to force remount when filters change
  const listKey = `${userInput}-${grantVestmentInput}-${types.join(",")}-${formattedStartDate || "null"
    }-${formattedEndDate || "null"}`;

  return (
    <Card sx={{ boxShadow: "none" }}>
      <CardContent>
        <Typography sx={{ color: `neutral.main`, p: 1 }} variant="subtitle1">
          Grant Vestment Interactions
        </Typography>
        <Grid container justifyContent="flex-end">
          <Button
            sx={{ backgroundColor: accent[600] }}
            onClick={exportToExcel}
            startIcon={<FileDownloadIcon />}
            variant="contained"
            color="primary"
            size="large"
          >
            Export
          </Button>
        </Grid>
        <Grid sx={{ my: 1 }} container spacing={2}>
          {typesFilter.map((item, index) => {
            const isSelected =
              (item === "By User" && showUserInput) ||
              (item === "By GrantVestment" && showGrantVestmentInput) ||
              (item === "By Date Range" && showDateRangeInput) ||
              types.includes(item);

            return (
              <Grid key={index} item>
                <Button
                  onClick={() => {
                    handleTypeChange(item);
                  }}
                  startIcon={isSelected ? <EyeIcon /> : <EyeClosedIcon />}
                  variant={isSelected ? "contained" : "outlined"}
                  color="primary"
                  size="large"
                >
                  {item}
                </Button>
              </Grid>
            );
          })}
        </Grid>

        {showUserInput && (
          <TextField
            fullWidth
            label="Enter User Name"
            value={userInput}
            onChange={(e) => {
              setUserInput(e.target.value);
            }}
            sx={{ my: 1 }}
          />
        )}
        {showGrantVestmentInput && (
          <TextField
            fullWidth
            label="Enter GrantVestment Name"
            value={grantVestmentInput}
            onChange={(e) => {
              setGrantVestmentInput(e.target.value);
            }}
            sx={{ my: 1 }}
          />
        )}
        {showDateRangeInput && (
          <>
            <TextField
              margin="normal"
              fullWidth
              InputLabelProps={{ hidden: true, shrink: true }}
              type="date"
              style={{ marginTop: "10px" }}
              inputProps={{
                max: new Date().toISOString().substring(0, 10),
              }}
              label="Start Date"
              sx={{ mt: 4 }}
              onChange={(event) => {
                const selectedDate = new Date(event.target.value);
                setFormattedStartDate(formatDateToCustomFormat(selectedDate));
                setStartDate(selectedDate);
              }}
            />
            <TextField
              margin="normal"
              fullWidth
              InputLabelProps={{ hidden: true, shrink: true }}
              type="date"
              style={{ marginTop: "10px" }}
              inputProps={{
                max: new Date().toISOString().substring(0, 10),
              }}
              label="End Date"
              sx={{ mt: 2 }}
              onChange={(event) => {
                setFormattedEndDate(
                  formatDateToCustomFormat(new Date(event.target.value))
                );
                setEndDate(new Date(event.target.value));
              }}
            />
          </>
        )}

        <GrantVestmentLikesList
          key={listKey}
          grantVestmentName={grantVestmentInput}
          userName={userInput}
          liked={types.includes("Liked")}
          disliked={types.includes("Disliked")}
          startDate={formattedStartDate}
          endDate={formattedEndDate}
          onDataUpdate={handleTableDataUpdate}
        />
      </CardContent>
    </Card>
  );
};
